import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { X, Loader2, ArrowDownLeft, ArrowUpRight, Wallet as WalletIcon, HandCoins, Calendar } from "lucide-react";
import api from "@/lib/api";

/**
 * WalletDetailModal
 * -----------------
 * Modal opened when a wallet card is clicked. Two tabs:
 *   - Transactions    (backend: kind=transactions → excludes CASH_FUNDING)
 *   - Funding History (backend: kind=funding      → only CASH_FUNDING)
 *
 * Date-range presets (Today / Yesterday / 7d / 30d / Custom) are applied
 * server-side alongside the currency + kind filters. Pagination via a
 * "Load more" button — the backend returns `total` + `has_more`.
 */

const CURRENCY_TONES = {
  USD: "bg-[#2BA8F0]",
  CAD: "bg-red-500",
  CLP: "bg-amber-500",
  DOP: "bg-blue-700",
  HTG: "bg-violet-600",
  EUR: "bg-indigo-500",
  BRL: "bg-emerald-600",
  MXN: "bg-teal-600",
};

const NO_DECIMAL = new Set(["CLP", "HTG"]);
const PAGE_SIZE = 50;

function fmt(amount, currency) {
  const n = Number(amount || 0);
  if (NO_DECIMAL.has(currency)) return Math.round(n).toLocaleString("en-US");
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function categoryLabel(cat) {
  const map = {
    CASH_FUNDING: "Wallet funding",
    CASH_TRANSFER_IN: "Transfer received",
    CASH_TRANSFER_OUT: "Transfer sent",
    CASH_WITHDRAW_IN: "Withdrawal received",
    CASH_WITHDRAW_OUT: "Withdrawal sent",
    SEND_CASH: "Send Cash",
  };
  return map[cat] || cat || "—";
}

// ---------- Date range helpers ----------
function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

/** Compute { from, to } ISO strings for a preset. `to` is EXCLUSIVE. */
function presetRange(key) {
  const today = startOfDay(new Date());
  const tomorrow = addDays(today, 1);
  if (key === "today") return { from: today.toISOString(), to: tomorrow.toISOString() };
  if (key === "yesterday") {
    const y = addDays(today, -1);
    return { from: y.toISOString(), to: today.toISOString() };
  }
  if (key === "7d") return { from: addDays(today, -6).toISOString(), to: tomorrow.toISOString() };
  if (key === "30d") return { from: addDays(today, -29).toISOString(), to: tomorrow.toISOString() };
  return { from: null, to: null }; // "all"
}

/** Turn yyyy-mm-dd (local) strings into ISO from/to with `to` exclusive. */
function customRange(fromStr, toStr) {
  const from = fromStr ? startOfDay(new Date(`${fromStr}T00:00:00`)).toISOString() : null;
  const to = toStr ? addDays(startOfDay(new Date(`${toStr}T00:00:00`)), 1).toISOString() : null;
  return { from, to };
}

// ---------- Component ----------

const PRESETS = [
  { key: "all",       label: "All time" },
  { key: "today",     label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d",        label: "7 days" },
  { key: "30d",       label: "30 days" },
  { key: "custom",    label: "Custom" },
];

export default function WalletDetailModal({ currency, balance, onClose }) {
  const [tab, setTab] = useState("transactions"); // "transactions" | "funding"

  // Date range state — Custom inputs start empty so clicking "Custom"
  // without picking dates is equivalent to "All time" (no filter). Once
  // the user types a value, it kicks in immediately.
  const [preset, setPreset] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  // Computed range (memo so tab switches don't recompute unnecessarily)
  const range = useMemo(() => {
    if (preset === "custom") return customRange(customFrom, customTo);
    return presetRange(preset);
  }, [preset, customFrom, customTo]);

  // Per-tab data (backend paginated)
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // Ref mirror of rows.length so the memoised `load` callback always sees
  // the latest count without adding rows to its dep array (which would
  // recreate load on every append and re-fire the reset effect).
  const rowsLenRef = useRef(0);
  useEffect(() => { rowsLenRef.current = rows.length; }, [rows.length]);

  const load = useCallback(async (mode) => {
    // mode: "reset" — fresh page 1; "more" — append next page
    const isMore = mode === "more";
    if (isMore) setLoadingMore(true); else setLoading(true);
    try {
      const params = {
        currency,
        kind: tab === "funding" ? "funding" : "transactions",
        limit: PAGE_SIZE,
        skip: isMore ? rowsLenRef.current : 0,
      };
      if (range.from) params.date_from = range.from;
      if (range.to) params.date_to = range.to;
      const { data } = await api.get("/cash-ledger", { params });
      const items = data.items || [];
      setRows((prev) => (isMore ? [...prev, ...items] : items));
      setTotal(data.total || 0);
      setHasMore(!!data.has_more);
    } catch {
      if (!isMore) { setRows([]); setTotal(0); setHasMore(false); }
    } finally {
      if (isMore) setLoadingMore(false); else setLoading(false);
    }
  }, [currency, tab, range.from, range.to]);

  // Reload from scratch whenever filters or tab change.
  useEffect(() => { load("reset"); }, [load]);

  const tone = CURRENCY_TONES[currency] || "bg-slate-500";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
      data-testid="wallet-detail-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl bg-white rounded-lg shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`${tone} text-white px-5 py-4 flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <WalletIcon className="w-5 h-5" />
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-90 font-semibold">Wallet</div>
              <div className="text-2xl font-bold leading-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {currency} · {fmt(balance ?? 0, currency)}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="wallet-detail-close"
            className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 flex">
          <button
            data-testid="wallet-detail-tab-transactions"
            onClick={() => setTab("transactions")}
            className={
              "flex-1 px-4 py-3 text-sm font-semibold border-b-2 transition-colors " +
              (tab === "transactions"
                ? "border-[#0F62FE] text-[#0F62FE] bg-[#EDF5FF]"
                : "border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-50")
            }
          >
            Transactions
          </button>
          <button
            data-testid="wallet-detail-tab-funding"
            onClick={() => setTab("funding")}
            className={
              "flex-1 px-4 py-3 text-sm font-semibold border-b-2 transition-colors " +
              (tab === "funding"
                ? "border-[#0F62FE] text-[#0F62FE] bg-[#EDF5FF]"
                : "border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-50")
            }
          >
            Funding History
          </button>
        </div>

        {/* Date range filter */}
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 space-y-2" data-testid="wallet-detail-range">
          <div className="flex items-center gap-1 flex-wrap">
            <Calendar className="w-3.5 h-3.5 text-slate-400 mr-1" />
            {PRESETS.map((p) => (
              <button
                key={p.key}
                data-testid={`wallet-detail-preset-${p.key}`}
                onClick={() => setPreset(p.key)}
                className={
                  "h-7 px-2.5 rounded-md text-[11px] font-semibold border transition-colors " +
                  (preset === p.key
                    ? "bg-[#0F62FE] text-white border-[#0F62FE]"
                    : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50")
                }
              >
                {p.label}
              </button>
            ))}
            <span className="ml-auto text-[11px] text-slate-500 font-medium" data-testid="wallet-detail-total">
              {loading ? "…" : `${total.toLocaleString()} entries`}
            </span>
          </div>
          {preset === "custom" && (
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="date"
                value={customFrom}
                max={customTo}
                onChange={(e) => setCustomFrom(e.target.value)}
                data-testid="wallet-detail-custom-from"
                className="h-8 rounded-md border border-slate-300 bg-white text-xs px-2 focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
              />
              <span className="text-xs text-slate-400">→</span>
              <input
                type="date"
                value={customTo}
                min={customFrom}
                onChange={(e) => setCustomTo(e.target.value)}
                data-testid="wallet-detail-custom-to"
                className="h-8 rounded-md border border-slate-300 bg-white text-xs px-2 focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
              />
            </div>
          )}
        </div>

        {/* List */}
        <div className="max-h-[52vh] overflow-y-auto" data-testid={`wallet-detail-list-${tab}`}>
          {loading ? (
            <div className="p-8 text-center text-slate-500 text-sm">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm">
              {tab === "funding" ? "No funding events in this range." : "No transactions in this range."}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-widest sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">Date</th>
                  <th className="text-left px-4 py-2 font-semibold">Category</th>
                  <th className="text-left px-4 py-2 font-semibold">Note</th>
                  <th className="text-right px-4 py-2 font-semibold">Amount</th>
                  <th className="text-right px-4 py-2 font-semibold">Balance after</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => {
                  const isCredit = r.type === "CREDIT";
                  return (
                    <tr key={r.id} className="hover:bg-slate-50">
                      <td className="px-4 py-2 text-slate-500 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                      <td className="px-4 py-2">
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-700">
                          {r.category === "CASH_FUNDING" ? (
                            <HandCoins className="w-3.5 h-3.5 text-emerald-600" />
                          ) : isCredit ? (
                            <ArrowDownLeft className="w-3.5 h-3.5 text-emerald-600" />
                          ) : (
                            <ArrowUpRight className="w-3.5 h-3.5 text-rose-600" />
                          )}
                          {categoryLabel(r.category)}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-slate-600 max-w-xs truncate" title={r.note || ""}>
                        {r.note || "—"}
                      </td>
                      <td className={"px-4 py-2 text-right font-mono font-semibold " + (isCredit ? "text-emerald-700" : "text-rose-700")}>
                        {isCredit ? "+" : "−"}{fmt(r.amount, r.currency || currency)}
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-slate-500">
                        {fmt(r.balance_after, r.currency || currency)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {hasMore && !loading && (
            <div className="px-4 py-3 border-t border-slate-100 flex justify-center">
              <button
                data-testid="wallet-detail-load-more"
                onClick={() => load("more")}
                disabled={loadingMore}
                className="inline-flex items-center gap-2 h-9 px-4 rounded-md border border-slate-300 bg-white text-slate-700 text-xs font-semibold hover:bg-slate-50 disabled:opacity-40"
              >
                {loadingMore && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Load more · {rows.length}/{total.toLocaleString()}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
